// Filters and groups tasks delegated to others.
import { useMemo } from 'react';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';

export const useDelegatedTasks = () => {
  const { filteredTasks, tasksMap } = useTasksContext();

  const delegatedTasksByPerson = useMemo(() => {
    if (!filteredTasks || filteredTasks.length === 0) {
      return {};
    }

    const delegatedTasks = filteredTasks.filter(
      (task) => !task.completed && task.person && task.status !== 'Backlog',
    );

    // Updated function to get the direct parent task as a breadcrumb object.
    const getBreadcrumb = (task) => {
      if (!task.parentId) return null;
      const parentTask = tasksMap.get(task.parentId);
      if (!parentTask) return null;
      // Return an object with the parent's ID and title.
      return { id: parentTask.id, title: parentTask.title };
    };

    const groupedTasks = delegatedTasks.reduce((acc, task) => {
      const person = task.person;
      if (!acc[person]) {
        acc[person] = [];
      }

      const breadcrumb = getBreadcrumb(task);

      // Only add breadcrumb if it exists
      const taskWithBreadcrumb = breadcrumb
        ? { ...task, sortBreadcrumb: breadcrumb }
        : task;

      acc[person].push(taskWithBreadcrumb);
      return acc;
    }, {});

    // Sort tasks within each person's group.
    for (const person in groupedTasks) {
      groupedTasks[person].sort((a, b) => {
        const breadcrumbA = a.sortBreadcrumb?.title || '';
        const breadcrumbB = b.sortBreadcrumb?.title || '';
        if (breadcrumbA < breadcrumbB) return -1;
        if (breadcrumbA > breadcrumbB) return 1;
        if (a.title < b.title) return -1;
        if (a.title > b.title) return 1;
        return 0;
      });
    }

    return groupedTasks;
  }, [filteredTasks, tasksMap]);

  return delegatedTasksByPerson;
};
